/**
 * @voltxampere/schema — Circuit (top-level aggregator)
 *
 * Per SPECS §1.1. Root schema the entire persistence pipeline hangs off:
 * `.vxa/circuit.json`, IndexedDB records, worker request payloads, and
 * URL-hash shares all parse to a `Circuit`.
 *
 * Cross-entity invariants (netId references, ground-net uniqueness,
 * terminal-count matching component library, parameter shape per
 * component type) are enforced by `validateCircuitInvariants` in
 * `../invariants.ts`, not by Zod alone — those rules reach beyond a
 * single entity.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { z } from "zod";
import { SemverSchema, UUIDSchema } from "./_primitives.js";
import { NodeSchema } from "./node.js";
import { NetSchema } from "./net.js";
import { ComponentInstanceSchema } from "./component.js";
import { WireSchema } from "./wire.js";
import { GroupSchema } from "./group.js";
import { AnnotationSchema } from "./annotation.js";
import { AssertionSchema } from "./assertion.js";
import { SimulationSettingsSchema } from "./simulation-settings.js";
import { ViewportStateSchema } from "./viewport-state.js";
export const CircuitSchema = z
    .object({
    schemaVersion: SemverSchema,
    id: UUIDSchema,
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    createdAt: z.number().int().positive(),
    updatedAt: z.number().int().positive(),
    nodes: z.array(NodeSchema),
    nets: z.array(NetSchema),
    components: z.array(ComponentInstanceSchema),
    wires: z.array(WireSchema),
    groups: z.array(GroupSchema).default([]),
    annotations: z.array(AnnotationSchema).default([]),
    assertions: z.array(AssertionSchema).default([]),
    simulationSettings: SimulationSettingsSchema.default({
        defaultAnalysis: "dc",
        dc: {
            temperature: 27,
            gmin: 1e-12,
            reltol: 1e-3,
            abstol: 1e-12,
            maxIterations: 100,
        },
        transient: {
            tstart: 0,
            tstop: 1e-3,
            method: "trap",
            reltol: 1e-3,
            abstol: 1e-12,
        },
        ac: {
            start: 1,
            stop: 1e9,
            points: 100,
            sweep: "dec",
        },
    }),
    viewport: ViewportStateSchema.optional(),
    metadata: z.record(z.unknown()).default({}),
})
    .strict();
//# sourceMappingURL=circuit.js.map